// src/store/thunks/karigar/karigarThunks.js

import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../services/api";

const BASE_URL = "";

// ========== FETCH KARIGARS ==========
export const fetchKarigars = createAsyncThunk(
  "karigar/karigar/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/admin-karigar`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== FETCH KARIGAR BY ID ==========
export const fetchKarigarById = createAsyncThunk(
  "karigar/karigar/fetchById",
  async (karigarId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/admin-karigar/${karigarId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== CREATE KARIGAR ==========
export const createKarigar = createAsyncThunk(
  "karigar/karigar/create",
  async (karigarData, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/admin-karigar`, karigarData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== UPDATE KARIGAR ==========
export const updateKarigar = createAsyncThunk(
  "karigar/karigar/update",
  async ({ karigarId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${BASE_URL}/admin-karigar/${karigarId}`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== UPDATE KARIGAR STATUS ==========
export const updateKarigarStatus = createAsyncThunk(
  "karigar/karigar/updateStatus",
  async ({ karigarId, status, adminMessage }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `${BASE_URL}/admin-karigar/${karigarId}/status`,
        {
          status,
          adminMessage,
        }
      );
      return { karigarId, status, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== DELETE KARIGAR ==========
export const deleteKarigar = createAsyncThunk(
  "karigar/karigar/delete",
  async (karigarId, { rejectWithValue }) => {
    try {
      await api.delete(`${BASE_URL}/admin-karigar/${karigarId}`);
      return { karigarId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== FETCH KARIGARS BY BRANCH ==========
export const fetchKarigarsByBranch = createAsyncThunk(
  "karigar/karigar/fetchByBranch",
  async ({ branchCode, params = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${BASE_URL}/admin-karigar/branch/${branchCode}`,
        { params }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== FETCH KARIGAR AVAILABILITY ==========
/**
 * Get single karigar availability with calendar
 * Route: GET /v1/karigar-availability/admin/karigar/:karigarId
 */
export const fetchKarigarAvailability = createAsyncThunk(
  "karigar/karigar/fetchAvailability",
  async ({ karigarId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${BASE_URL}/karigar-availability/admin/karigar/${karigarId}`,
        { params }
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== FETCH BULK KARIGAR AVAILABILITY ==========
/**
 * Get availability for multiple karigars (for assignment selection)
 * Route: POST /v1/karigar-availability/admin/bulk
 *
 * Returns productKarigars (from capableKarigars list) first, then otherKarigars
 *
 * @param {Object} data - Request data
 * @param {string} data.branchCode - Branch code
 * @param {string[]} data.availableBranches - Available branch codes
 * @param {string} data.productType - Product type
 * @param {string} data.productSubType - Product subtype
 * @param {string} data.workType - Work type
 * @param {string} data.complexity - Complexity level
 * @param {string[]} data.requiredSkills - Required skills
 * @param {string} data.targetDate - Target completion date
 * @param {Object[]} data.capableKarigars - Product's capable karigars
 * @param {Object} data.estimatedMakingTime - Product's estimated making time
 */
export const fetchBulkKarigarAvailability = createAsyncThunk(
  "karigar/karigar/fetchBulkAvailability",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${BASE_URL}/karigar-availability/admin/bulk`,
        data
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== FETCH ESTIMATED HOURS FOR PRODUCT ==========
/**
 * Get estimated hours for a specific product/work type
 * Route: GET /v1/karigar-availability/admin/karigar/:karigarId/estimate
 *
 * @param {Object} params
 * @param {string} params.karigarId - Karigar ID
 * @param {Object} params.params - Query parameters
 * @param {string} params.params.productType - Product type
 * @param {string} params.params.productSubType - Product subtype
 * @param {string} params.params.workType - Work type
 * @param {string} params.params.complexity - Complexity level (simple/medium/complex)
 * @param {number} params.params.weight - Product weight
 */
export const fetchEstimatedHoursForProduct = createAsyncThunk(
  "karigar/karigar/fetchEstimate",
  async ({ karigarId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${BASE_URL}/karigar-availability/admin/karigar/${karigarId}/estimate`,
        { params }
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== ADD KARIGAR TO BRANCH ==========
export const addKarigarToBranch = createAsyncThunk(
  "karigar/karigar/addToBranch",
  async ({ karigarId, branchData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${BASE_URL}/admin-karigar/${karigarId}/branches`,
        branchData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== REMOVE KARIGAR FROM BRANCH ==========
export const removeKarigarFromBranch = createAsyncThunk(
  "karigar/karigar/removeFromBranch",
  async ({ karigarId, branchCode }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `${BASE_URL}/admin-karigar/${karigarId}/branches/${branchCode}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== UPDATE KARIGAR SKILLS ==========
export const updateKarigarSkills = createAsyncThunk(
  "karigar/karigar/updateSkills",
  async ({ karigarId, skills }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${BASE_URL}/admin-karigar/${karigarId}/skills`,
        { skills }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== VERIFY KARIGAR SKILL ==========
export const verifyKarigarSkill = createAsyncThunk(
  "karigar/karigar/verifySkill",
  async (
    { karigarId, skillCategory, verificationDetails },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `${BASE_URL}/admin-karigar/${karigarId}/verify-skill`,
        { skillCategory, verificationDetails }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== FETCH KARIGAR STATS ==========
export const fetchKarigarStats = createAsyncThunk(
  "karigar/karigar/fetchStats",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/admin-karigar/dashboard`, {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== UPDATE KARIGAR WORK PREFERENCES ==========
export const updateWorkPreferences = createAsyncThunk(
  "karigar/karigar/updateWorkPreferences",
  async ({ karigarId, preferences }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${BASE_URL}/admin-karigar/${karigarId}/work-preferences`,
        preferences
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== SEARCH KARIGARS ==========
export const searchKarigars = createAsyncThunk(
  "karigar/karigar/search",
  async (searchParams, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/admin-karigar/search`, {
        params: searchParams,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== GET AVAILABLE KARIGARS ==========
export const fetchAvailableKarigars = createAsyncThunk(
  "karigar/karigar/fetchAvailable",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/admin-karigar/available`, {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== GET PERFORMANCE REPORT ==========
export const fetchPerformanceReport = createAsyncThunk(
  "karigar/karigar/fetchPerformanceReport",
  async ({ karigarId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${BASE_URL}/admin-karigar/${karigarId}/performance-report`,
        { params }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== BULK OPERATIONS ==========

// Approve multiple karigars
export const approveMultipleKarigars = createAsyncThunk(
  "karigar/karigar/approveMultiple",
  async (karigarIds, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${BASE_URL}/admin-karigar/bulk/approve`,
        {
          karigarIds,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Suspend multiple karigars
export const suspendMultipleKarigars = createAsyncThunk(
  "karigar/karigar/suspendMultiple",
  async ({ karigarIds, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${BASE_URL}/admin-karigar/bulk/suspend`,
        {
          karigarIds,
          reason,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Delete multiple karigars
export const deleteMultipleKarigars = createAsyncThunk(
  "karigar/karigar/deleteMultiple",
  async (karigarIds, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `${BASE_URL}/admin-karigar/bulk/delete`,
        {
          data: { karigarIds },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== PASSWORD MANAGEMENT ==========

// Set karigar password
export const setKarigarPassword = createAsyncThunk(
  "karigar/karigar/setPassword",
  async ({ karigarId, password }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${BASE_URL}/admin-karigar/${karigarId}/set-password`,
        { password }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Reset karigar password
export const resetKarigarPassword = createAsyncThunk(
  "karigar/karigar/resetPassword",
  async ({ karigarId, sendSms = true }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${BASE_URL}/admin-karigar/${karigarId}/reset-password`,
        { sendSms }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Check if karigar has password
export const checkKarigarPassword = createAsyncThunk(
  "karigar/karigar/checkPassword",
  async (karigarId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${BASE_URL}/admin-karigar/${karigarId}/password-status`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ========== VERIFICATION ==========

// Verify bank details
export const verifyBankDetails = createAsyncThunk(
  "karigar/karigar/verifyBankDetails",
  async ({ karigarId, verificationNotes }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${BASE_URL}/admin-karigar/${karigarId}/verify-bank`,
        { verificationNotes }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);
